import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { History, ChevronDown, BookMarked, Clock } from "lucide-react";
import { api } from "@/lib/api";

// "Past semesters" — a look back at archived terms. Lifetime rewards keep growing separately.
export default function PastTerms() {
  const [archives, setArchives] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [classesById, setClassesById] = useState({});

  useEffect(() => {
    api.get("/terms/archives").then((r) => setArchives(r.data.archives || [])).catch(() => setArchives([]));
  }, []);

  const toggle = async (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    if (!classesById[id]) {
      try {
        const r = await api.get(`/terms/archives/${id}`);
        setClassesById((m) => ({ ...m, [id]: r.data.classes || [] }));
      } catch {
        setClassesById((m) => ({ ...m, [id]: [] }));
      }
    }
  };

  if (!archives || archives.length === 0) return null;

  return (
    <div className="mb-6" data-testid="past-terms">
      <h2 className="font-display text-lg font-semibold mb-1 flex items-center gap-2">
        <History size={17} /> Past semesters
      </h2>
      <p className="text-xs text-muted-foreground mb-3">
        Your archived schedules. Points, streaks and badges keep growing separately.
      </p>
      <div className="rounded-2xl border border-border overflow-hidden">
        {archives.map((a) => {
          const open = openId === a.id;
          const cls = classesById[a.id];
          return (
            <div key={a.id} className="border-b border-border last:border-0" data-testid={`past-term-${a.id}`}>
              <button
                onClick={() => toggle(a.id)}
                data-testid={`past-term-toggle-${a.id}`}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary/50 transition-colors"
              >
                <span className="w-9 h-9 rounded-lg bg-brand/10 text-brand flex items-center justify-center shrink-0">
                  <BookMarked size={17} strokeWidth={1.8} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{a.term_name || "Previous term"}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                    {a.count} {a.count === 1 ? "class" : "classes"}
                    <span>·</span>
                    <Clock size={11} />
                    archived {a.archived_at ? new Date(a.archived_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}
                  </div>
                </div>
                <ChevronDown size={17} className={`shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {open && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden bg-secondary/30"
                  >
                    <div className="px-4 py-3 grid gap-1.5" data-testid={`past-term-classes-${a.id}`}>
                      {cls === undefined ? (
                        <div className="text-xs text-muted-foreground">Loading…</div>
                      ) : cls.length === 0 ? (
                        <div className="text-xs text-muted-foreground">No class details saved.</div>
                      ) : (
                        cls.map((c, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <span className="font-semibold truncate">{c.name}</span>
                            <span className="mono text-xs text-muted-foreground truncate">
                              {(c.days || []).join("")} {c.start_time}{c.end_time ? `–${c.end_time}` : ""}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
